import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  computed,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CommunicationApiResponseSuccess,
  MessageContent,
  MessageRequestMeta,
  MessageRequestWithMeta,
  Thread,
} from '../../interface';
import { MessageRequestComponent, SkyproMessagesModule } from '@skyeng/libs/skypro/messages';
import { CommunicationFormService } from './communication-form.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { registerTranslationsManuallyStatic } from '@skyeng/libs/shared/i18n';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { combineLatestWith, map, Observable, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { DsButtonModule } from '@skyeng/libs/shared/ui-kit-v2/button';
import { DsIconModule } from '@skyeng/libs/shared/ui-kit-v2/icon';
import { TypografModule } from '@skyeng/libs/skypro/misc';
import entranslation from '../../translation/en.json';
import rutranslation from '../../translation/ru.json';

const MAX_MESSAGE_LENGTH = 4096;

enum ActionType {
  submit = 'submit',
  extraSubmit = 'extraSubmit',
}

enum AttachmentFileType {
  image = 'image',
  video = 'video',
  other = 'other',
}

enum AttachmentStatus {
  uploading = 'uploading',
  uploaded = 'uploaded',
  error = 'error',
}

interface AttachmentUpload {
  uuid: string;
  mimeType?: string;
  type?: AttachmentFileType;
  url?: string;
  previewUrl?: string;
  title: string;
  status: AttachmentStatus;
}

@DecorateUntilDestroy()
@Component({
  selector: 'skypro-communication-form',
  templateUrl: 'communication-form.html',
  styleUrls: ['communication-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SkyproMessagesModule, DsButtonModule, DsIconModule, AsyncPipe, TranslocoModule, TypografModule],
})
export class CommunicationsFormComponent implements AfterViewInit {
  @Input() thread?: Thread;
  @Input() editMessageContent?: MessageContent;
  @Input() hasAutofocus = false;
  @Input() hasCallTutorButton = false;
  @Input() canAskTutorOrAI = false;
  @Input() allowedAttachmentTypes: string[] | null;
  @Input() isMessengerMode = false;
  @Input() needSaveInProgressText = false;
  @Input() localStorageKey: string | null = null;

  readonly inputDisabledPlaceholder = input<string | null>(null);

  @Output() finishEditing = new EventEmitter<void>();
  @Output() createdThread = new EventEmitter<void>();
  @Output() createdReplyInThread = new EventEmitter<void>();

  @ViewChild('messageRequestComponent') messageRequestComponent: MessageRequestComponent;

  private translocoService = inject(TranslocoService);
  private communicationFormService = inject(CommunicationFormService);

  showSendingError = false;
  showMessageTooLongError$: Observable<number>;
  message$: Observable<string>;
  attachmentsUpload$: Observable<AttachmentUpload[]>;
  isSubmitDisabled$: Observable<boolean>;
  submitActionType = ActionType;

  isLoading$ = this.communicationFormService.isLoading$;

  readonly isTextareaDisabled = computed(() => this.inputDisabledPlaceholder() !== null);

  constructor() {
    registerTranslationsManuallyStatic('skyproCommunications', this.translocoService, {
      en: entranslation,
      ru: rutranslation,
    });
  }

  ngAfterViewInit(): void {
    this.message$ = this.messageRequestComponent.message$;
    this.attachmentsUpload$ = this.messageRequestComponent.attachmentsUpload$;

    this.showMessageTooLongError$ = this.message$.pipe(
      map(message => (message?.length > MAX_MESSAGE_LENGTH ? message.length - MAX_MESSAGE_LENGTH : 0)),
    );

    this.isSubmitDisabled$ = this.isLoading$.pipe(
      combineLatestWith(this.showMessageTooLongError$, this.message$, this.attachmentsUpload$),
      map(([isLoading, showMessageTooLongError, message, attachmentsUpload]) => {
        if (isLoading) {
          return true;
        }

        if (showMessageTooLongError) {
          return true;
        }

        return message.length === 0 && attachmentsUpload.length === 0;
      }),
    );
  }

  private buildMeta(canAskTutorOrAI: boolean, actionType: ActionType): MessageRequestMeta {
    if (canAskTutorOrAI) {
      return { needRealTutorAnswer: actionType === ActionType.extraSubmit };
    }

    return {};
  }

  getPlaceholder(): string {
    const inputDisabledPlaceholder = this.inputDisabledPlaceholder();
    if (inputDisabledPlaceholder !== null) {
      return inputDisabledPlaceholder;
    }

    if (this.isMessengerMode) {
      return 'Сообщение';
    }

    return this.canAskTutorOrAI && this.hasCallTutorButton
      ? 'Вы можете написать сообщение ИИ, он ответит быстрее наставника'
      : 'Напишите сообщение, и вам сразу ответит ИИ-помощник, или еще 2 сообщения — и придет наставник, он ответит за 5 часов';
  }

  submit(actionType: ActionType): void {
    const message = this.messageRequestComponent.getMessage();

    if (!message) {
      return;
    }

    const meta = this.buildMeta(this.canAskTutorOrAI, actionType);

    this.wrapErrorHandle(this.createRequestStream({ ...message, meta }))
      .pipe(
        tap({
          complete: () => this.messageRequestComponent.clear(),
        }),
        takeUntilDestroyed(this),
      )
      .subscribe();
  }

  private createRequestStream(message: MessageRequestWithMeta): Observable<CommunicationApiResponseSuccess> {
    const messageId = this.editMessageContent?.id;
    if (messageId) {
      return (
        this.thread
          ? this.communicationFormService.editMessageInThread(message, messageId)
          : this.communicationFormService.editThread(message, messageId)
      ).pipe(
        tap({
          complete: () => this.finishEditing.emit(),
        }),
      );
    }
    return this.thread
      ? this.communicationFormService.replyInThread(message, this.thread.id).pipe(
          tap({
            complete: () => this.createdReplyInThread.emit(),
          }),
        )
      : this.communicationFormService.createThread(message).pipe(
          tap({
            complete: () => this.createdThread.emit(),
          }),
        );
  }

  private wrapErrorHandle(
    request$: Observable<CommunicationApiResponseSuccess>,
  ): Observable<CommunicationApiResponseSuccess> {
    this.showSendingError = false;
    return request$.pipe(
      tap({
        error: () => {
          this.showSendingError = true;
        },
      }),
    );
  }
}
